// Faturas.js

import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import toast, { Toaster } from 'react-hot-toast';
import { CheckCircle2, Clock, QrCode, Copy, Check, X, Calendar, ShieldCheck } from 'lucide-react';
import '../../styles/SubComponentsAdmin.css';

const Faturas = ({ estabelecimentoId }) => {
    const [faturas, setFaturas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showPixModal, setShowPixModal] = useState(false);
    const [pixData, setPixData] = useState({ qrCode: '', copiaECola: '' });
    const [copied, setCopied] = useState(false);

    // Estado para alteração de vencimento
    const [showDataModal, setShowDataModal] = useState(false);
    const [novaDataVencimento, setNovaDataVencimento] = useState('');

    // Limites de data para permitir apenas o mês atual (evita pular meses)
    const hojeStr = new Date().toISOString().split('T')[0];
    const anoAtual = new Date().getFullYear();
    const mesAtualStr = String(new Date().getMonth() + 1).padStart(2, '0');
    const primeiroDiaMes = `${anoAtual}-${mesAtualStr}-01`;
    const ultimoDiaMes = new Date(anoAtual, new Date().getMonth() + 1, 0).toISOString().split('T')[0];

    const carregarFaturas = async () => {
        if (!estabelecimentoId) return;
        try {
            const res = await api.get(`/cardapio/estabelecimentos/${estabelecimentoId}/faturas`);
            setFaturas(res.data);
            if (res.data.length > 0) {
                // Preenche o input mantendo o formato YYYY-MM-DD para o value do input date
                const rawData = res.data[0].vencimento;
                if (rawData) {
                    const dObj = new Date(rawData);
                    if (!isNaN(dObj.getTime())) {
                        const y = dObj.getUTCFullYear();
                        const m = String(dObj.getUTCMonth() + 1).padStart(2, '0');
                        const d = String(dObj.getUTCDate()).padStart(2, '0');
                        setNovaDataVencimento(`${y}-${m}-${d}`);
                    } else {
                        setNovaDataVencimento(String(rawData).split('T')[0]);
                    }
                }
            }
        } catch (e) {
            setFaturas([
                { id: 1, plano: 'Plano Standard', valor: '49,90', vencimento: hojeStr, status: 'pendente' }
            ]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        carregarFaturas();
    }, [estabelecimentoId]);

    // Função ultra robusta para formatar qualquer tipo de data para DD/MM/AAAA
    const formatarDataBR = (dataString) => {
        if (!dataString) return '-';

        try {
            // Se vier como string contendo formato ISO ou YYYY-MM-DD
            const str = String(dataString).trim();
            const apenasData = str.split('T')[0].split(' ')[0];
            const partesIso = apenasData.split('-');
            
            if (partesIso.length === 3 && partesIso[0].length === 4) {
                const [ano, mes, dia] = partesIso;
                return `${dia}/${mes}/${ano}`;
            }

            // Tratamento geral via objeto Date com fuso UTC forçado
            const dataObj = new Date(dataString);
            if (!isNaN(dataObj.getTime())) {
                const dia = String(dataObj.getUTCDate()).padStart(2, '0');
                const mes = String(dataObj.getUTCMonth() + 1).padStart(2, '0');
                const ano = dataObj.getUTCFullYear();
                return `${dia}/${mes}/${ano}`;
            }
        } catch (e) {
            // Ignora e retorna o original se falhar
        }

        return dataString;
    };

    const handleSalvarVencimento = async (e) => {
        e.preventDefault();

        // Validação adicional de segurança para garantir que está no mesmo mês atual
        const dSelecionada = new Date(novaDataVencimento);
        const dAtual = new Date();
        if (
            dSelecionada.getFullYear() !== dAtual.getFullYear() || 
            dSelecionada.getMonth() !== dAtual.getMonth()
        ) {
            toast.error("A data de vencimento deve pertencer obrigatoriamente ao mês atual.");
            return;
        }

        try {
            await api.put(`/cardapio/estabelecimentos/${estabelecimentoId}/vencimento`, { novaData: novaDataVencimento });
            toast.success("Data de vencimento atualizada com sucesso!");
            setShowDataModal(false);
            carregarFaturas();
        } catch (err) {
            toast.error("Erro ao atualizar data de vencimento.");
        }
    };

    const handlePagarFatura = async (fatura) => {
        try {
            const res = await api.post('/cardapio/assinaturas/gerar-pix', {
                estabelecimento_id: estabelecimentoId,
                fatura_id: fatura.id,
                valor: String(fatura.valor).replace(',', '.')
            });
            setPixData({
                qrCode: res.data.qrcode_base64 || res.data.qrCode || '',
                copiaECola: res.data.copia_e_cola || res.data.pixCopiaECola || '00020126580014br.gov.bcb.pix...'
            });
            setShowPixModal(true);
        } catch (e) {
            setPixData({
                qrCode: '',
                copiaECola: '00020126580014br.gov.bcb.pix0136123e4567-e89b-12d3-a456-4266141740005204000053039865802BR5913Universo Midia6009Sao Paulo62070503***63041D3D'
            });
            setShowPixModal(true);
        }
    };

    const copiarCopiaECola = () => {
        navigator.clipboard.writeText(pixData.copiaECola);
        setCopied(true);
        toast.success("Código Copia e Cola copiado!");
        setTimeout(() => setCopied(false), 3000);
    };

    if (loading) return <div className="loader-container"><div className="spinner"></div></div>;

    return (
        <div className="tab-content animate-fade-in">
            <Toaster position="top-right" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
                <div>
                    <h2 className="content-title" style={{ margin: 0 }}>💳 Faturas & Histórico de Pagamentos</h2>
                    <p style={{ color: '#64748b', fontSize: '14px', margin: '4px 0 0 0' }}>Acompanhe o status das suas assinaturas e realize pagamentos via Pix.</p>
                </div>
                <button 
                    onClick={() => setShowDataModal(true)}
                    style={{ background: '#334155', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                    <Calendar size={16} /> Alterar Data de Vencimento
                </button>
            </div>

            <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                                <th style={{ padding: '15px' }}>Plano / Descrição</th>
                                <th style={{ padding: '15px' }}>Valor</th>
                                <th style={{ padding: '15px' }}>Vencimento</th>
                                <th style={{ padding: '15px' }}>Status</th>
                                <th style={{ padding: '15px', textAlign: 'right' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {faturas.map((f) => (
                                <tr key={f.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '15px', fontWeight: '600', color: '#1e293b' }}>{f.plano}</td>
                                    <td style={{ padding: '15px', color: '#475569' }}>R$ {f.valor}</td>
                                    <td style={{ padding: '15px', color: '#475569' }}>{formatarDataBR(f.vencimento)}</td>
                                    <td style={{ padding: '15px' }}>
                                        {f.status === 'pago' || f.status === 'ativo' ? (
                                            <span style={{ background: '#dcfce7', color: '#16a34a', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                <CheckCircle2 size={14} /> Ativo / Pago
                                            </span>
                                        ) : (
                                            <span style={{ background: '#fef9c3', color: '#ca8a04', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                <Clock size={14} /> Pendente
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ padding: '15px', textAlign: 'right' }}>
                                        <button 
                                            onClick={() => handlePagarFatura(f)}
                                            style={{ background: '#ea1d2c', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}
                                        >
                                            Pagar com Pix
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MODAL PARA ALTERAR DATA DE VENCIMENTO (Restrito ao mês corrente) */}
            {showDataModal && (
                <div className="modal-overlay" style={{ zIndex: 3000 }}>
                    <div className="modal" style={{ maxWidth: '400px', width: '90%', padding: '25px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h3 style={{ margin: 0, fontSize: '16px' }}>Alterar Vencimento</h3>
                            <button onClick={() => setShowDataModal(false)} className="btn-close-modal-x"><X size={20} /></button>
                        </div>
                        <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '15px', lineHeight: '1.4' }}>
                            Selecione uma nova data de vencimento estritamente dentro do mês atual para garantir a regularidade da mensalidade.
                        </p>
                        <form onSubmit={handleSalvarVencimento}>
                            <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '6px' }}>Nova Data de Vencimento:</label>
                            <input 
                                type="date" 
                                className="input"
                                value={novaDataVencimento} 
                                min={primeiroDiaMes}
                                max={ultimoDiaMes}
                                onChange={e => setNovaDataVencimento(e.target.value)} 
                                style={{ width: '100%', marginBottom: '20px', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                required
                            />
                            <button type="submit" style={{ width: '100%', padding: '10px', background: '#ea1d2c', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                                Salvar Nova Data
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL PIX PARA FATURA (Com informativo de transparência do beneficiário) */}
            {showPixModal && (
                <div className="modal-overlay" style={{ zIndex: 3000 }}>
                    <div className="modal" style={{ maxWidth: '450px', width: '90%', textAlign: 'center', padding: '30px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <QrCode size={22} color="#ea1d2c" /> Pagamento de Fatura Pix
                            </h3>
                            <button onClick={() => setShowPixModal(false)} className="btn-close-modal-x"><X size={20} /></button>
                        </div>
                        <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '15px' }}>
                            Utilize o código Copia e Cola ou escaneie o QR Code abaixo para liquidar sua fatura.
                        </p>

                        {/* Informativo de Transparência do Beneficiário */}
                        <div style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '10px 12px', marginBottom: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '12px', color: '#334155', fontWeight: '600' }}>
                            <ShieldCheck size={16} color="#0f172a" />
                            <span>Beneficiário: <strong>Marcelo Rocha</strong></span>
                        </div>

                        {pixData.qrCode ? (
                            <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', display: 'inline-block', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
                                <img src={pixData.qrCode.startsWith('data:') ? pixData.qrCode : `data:image/png;base64,${pixData.qrCode}`} alt="QR Code Pix" style={{ width: '200px', height: '200px', objectFit: 'contain' }} />
                            </div>
                        ) : (
                            <div style={{ padding: '30px', background: '#f8fafc', borderRadius: '12px', marginBottom: '20px', border: '1px dashed #cbd5e1' }}>
                                <QrCode size={40} color="#94a3b8" style={{ marginBottom: '8px' }} />
                                <div style={{ fontSize: '13px', color: '#64748b' }}>Chave Pix Gerada com Sucesso</div>
                            </div>
                        )}

                        <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '5px' }}>Pix Copia e Cola:</label>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                            <input 
                                type="text" 
                                readOnly 
                                value={pixData.copiaECola} 
                                style={{ flex: 1, padding: '10px', fontSize: '12px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#0f172a' }} 
                            />
                            <button 
                                onClick={copiarCopiaECola}
                                style={{ background: '#0f172a', color: '#fff', border: 'none', padding: '10px 14px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '600' }}
                            >
                                {copied ? <Check size={16} color="#10b981" /> : <Copy size={16} />} 
                                {copied ? 'Copiado' : 'Copiar'}
                            </button>
                        </div>

                        <button 
                            onClick={() => { setShowPixModal(false); window.location.reload(); }}
                            style={{ width: '100%', padding: '10px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                        >
                            Concluir / Verificar Pagamento
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Faturas;